if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /* ── Cart conflict rules ───────────────────────────────────────────────
         Harvested Today and Fresh Meat cannot be in the cart together.
         Eggs can be mixed with either type freely.
      ────────────────────────────────────────────────────────────────────── */
      static CONFLICT_MAP = {
        'Harvested Today': ['Fresh Meat'],
        'Fresh Meat': ['Harvested Today'],
      };

      // Normalize for comparison: trim, lowercase, strip non-alphanumerics
      // (handles trademark symbols, casing, extra whitespace, etc.)
      static normalizeType(value) {
        if (!value) return '';
        return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
      }

      // Pretty label for display
      static labelFor(type) {
        const n = ProductForm.normalizeType(type);
        if (n === ProductForm.normalizeType('Harvested Today')) return 'Harvested Today<sup>TM</sup>';
        if (n === ProductForm.normalizeType('Fresh Meat')) return 'Fresh Meat';
        return type || '';
      }

      async checkCartConflict(newProductType) {
        const newNorm = ProductForm.normalizeType(newProductType);
        // Build a normalized conflict list
        const conflictsWithRaw = Object.keys(ProductForm.CONFLICT_MAP).find(
          (k) => ProductForm.normalizeType(k) === newNorm
        );
        const conflictsWith = conflictsWithRaw ? ProductForm.CONFLICT_MAP[conflictsWithRaw] : null;
        if (!conflictsWith || conflictsWith.length === 0) return { hasConflict: false, existingType: null };
        const conflictsWithNorm = conflictsWith.map(ProductForm.normalizeType);

        let cart;
        try {
          const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          cart = await resp.json();
        } catch (e) {
          return { hasConflict: false, existingType: null }; // can't fetch cart — don't block
        }

        if (!cart.items || cart.items.length === 0) return { hasConflict: false, existingType: null };

        let existingType = null;
        const hasConflict = cart.items.some((item) => {
          const itemType = item.properties && item.properties['_product_type'];
          if (!itemType) return false;
          if (conflictsWithNorm.includes(ProductForm.normalizeType(itemType))) {
            existingType = itemType; // capture the actual stored value
            return true;
          }
          return false;
        });

        return { hasConflict, existingType };
      }

      showConflictModal(newProductType, existingType, onConfirm, onCancel) {
        // Inject styles once
        if (!document.getElementById('cart-conflict-modal-styles')) {
          const style = document.createElement('style');
          style.id = 'cart-conflict-modal-styles';
          style.textContent = `
            #cart-conflict-modal {
              position: fixed;
              inset: 0;
              z-index: 99999;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .cart-conflict-modal__overlay {
              position: absolute;
              inset: 0;
              background: rgba(0, 0, 0, 0.55);
            }
            .cart-conflict-modal__dialog {
              position: relative;
              background: #fff;
              border: 2px solid #ff69b4;
              border-radius: 8px;
              padding: 2rem 2rem 1.75rem;
              max-width: 480px;
              width: 90%;
              text-align: center;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
            }
            .cart-conflict-modal__title {
              font-size: 1.25rem;
              font-weight: 700;
              margin: 0 0 1rem;
            }
            .cart-conflict-modal__message {
              margin: 0 0 1.5rem;
              line-height: 1.6;
              color: #444;
              text-align: left;
            }
            .cart-conflict-modal__message p {
              margin: 0 0 0.85rem;
            }
            .cart-conflict-modal__message p:last-child {
              margin-bottom: 0;
            }
            .cart-conflict-modal__actions {
              display: flex;
              gap: 0.75rem;
              justify-content: center;
            }
          `;
          document.head.appendChild(style);
        }

        // Remove stale modal if present
        const existing = document.getElementById('cart-conflict-modal');
        if (existing) existing.remove();

        // Derive labels from the actual values, normalized — avoids exact-string mismatches
        const inCartLabel = ProductForm.labelFor(existingType);
        const blockedLabel = ProductForm.labelFor(newProductType);

        const modal = document.createElement('div');
        modal.id = 'cart-conflict-modal';
        modal.innerHTML = `
          <div class="cart-conflict-modal__overlay"></div>
          <div class="cart-conflict-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="cart-conflict-title">
            <p class="cart-conflict-modal__title" id="cart-conflict-title">Separate Orders Required</p>
            <div class="cart-conflict-modal__message">
              <p>You currently have <strong>${inCartLabel}</strong> items in your cart, which cannot be mixed with <strong>${blockedLabel}</strong>.</p>
              <p><strong>Harvested Today<sup>TM</sup></strong> items (prepared to order) and our <strong>Fresh Meat</strong> selection (ready for quick dispatch) require separate orders because they have a different journey to your door.</p>
              <p>Clicking <strong>Confirm</strong> will clear your current cart so you can proceed with this selection.</p>
            </div>
            <div class="cart-conflict-modal__actions">
              <button class="cart-conflict-modal__btn--confirm button button--primary">Confirm</button>
              <button class="cart-conflict-modal__btn--cancel button button--secondary">Cancel</button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        modal.querySelector('.cart-conflict-modal__btn--confirm').addEventListener('click', () => {
          modal.remove();
          onConfirm();
        });
        modal.querySelector('.cart-conflict-modal__btn--cancel').addEventListener('click', () => {
          modal.remove();
          onCancel();
        });
        modal.querySelector('.cart-conflict-modal__overlay').addEventListener('click', () => {
          modal.remove();
          onCancel();
        });
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const newProductType = this.dataset.productType || '';
        const { hasConflict, existingType } = await this.checkCartConflict(newProductType);

        if (hasConflict) {
          const userConfirmed = await new Promise((resolve) => {
            this.showConflictModal(
              newProductType,
              existingType,
              () => resolve(true),
              () => resolve(false)
            );
          });

          if (!userConfirmed) return;

          // Clear the cart before adding the new item
          try {
            await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
          } catch (e) {
            console.error('[CartConflict] Could not clear cart:', e);
          }
        }

        this.doAddToCart(evt);
      }

      doAddToCart(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}